import { Link } from "@klarify/i18n/navigation";
import { Trans, useLingui } from "@lingui/react/macro";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils";
import { buildPageList } from "./build-page-list";

type PaginationProps = {
	page: number;
	totalPages: number;
	hrefForPage: (page: number) => string;
};

type DirectoryPaginationProps = PaginationProps & {
	/**
	 * Accessible name for the nav. Omitted on surfaces that have no translated
	 * label of their own yet — an unnamed nav beats one named after a different
	 * directory.
	 */
	ariaLabel?: string;
};

/**
 * Numeric page links for any paginated public directory (therapists, blog
 * posts). `previous`/`next` are directory-neutral, so both surfaces read them
 * from this component's own namespace.
 */
export const DirectoryPagination = ({
	page,
	totalPages,
	hrefForPage,
	ariaLabel,
}: DirectoryPaginationProps) => {
	if (totalPages <= 1) {
		return null;
	}
	const pages = buildPageList(page, totalPages);
	const prevDisabled = page <= 1;
	const nextDisabled = page >= totalPages;

	return (
		<nav
			aria-label={ariaLabel}
			className="mt-10 flex flex-wrap items-center justify-center gap-2 sm:mt-12"
		>
			{prevDisabled ? (
				<span
					aria-disabled="true"
					className="inline-flex h-9 cursor-not-allowed items-center gap-1 rounded-md border border-klarify-neutral-200 bg-white px-3 text-klarify-neutral-300 text-sm"
				>
					<ChevronLeft className="h-4 w-4" />
					<Trans>Previous</Trans>
				</span>
			) : (
				<Link
					href={hrefForPage(page - 1)}
					rel="prev"
					className="inline-flex h-9 items-center gap-1 rounded-md border border-klarify-neutral-200 bg-white px-3 text-klarify-neutral-700 text-sm hover:border-klarify-forest-200 hover:text-klarify-forest-800"
				>
					<ChevronLeft className="h-4 w-4" />
					<Trans>Previous</Trans>
				</Link>
			)}
			{pages.map((p, idx) =>
				p === "…" ? (
					<span
						key={`gap-${idx}`}
						aria-hidden
						className="px-2 text-klarify-neutral-400 text-sm"
					>
						…
					</span>
				) : p === page ? (
					<span
						key={p}
						aria-current="page"
						className="inline-flex h-9 min-w-9 items-center justify-center rounded-md border border-klarify-forest-700 bg-klarify-forest-700 px-3 font-medium text-sm text-white"
					>
						{p}
					</span>
				) : (
					<Link
						key={p}
						href={hrefForPage(p)}
						className={cn(
							"inline-flex h-9 min-w-9 items-center justify-center rounded-md border border-klarify-neutral-200 bg-white px-3 text-klarify-neutral-700 text-sm hover:border-klarify-forest-200 hover:text-klarify-forest-800",
						)}
					>
						{p}
					</Link>
				),
			)}
			{nextDisabled ? (
				<span
					aria-disabled="true"
					className="inline-flex h-9 cursor-not-allowed items-center gap-1 rounded-md border border-klarify-neutral-200 bg-white px-3 text-klarify-neutral-300 text-sm"
				>
					<Trans>Next</Trans>
					<ChevronRight className="h-4 w-4" />
				</span>
			) : (
				<Link
					href={hrefForPage(page + 1)}
					rel="next"
					className="inline-flex h-9 items-center gap-1 rounded-md border border-klarify-neutral-200 bg-white px-3 text-klarify-neutral-700 text-sm hover:border-klarify-forest-200 hover:text-klarify-forest-800"
				>
					<Trans>Next</Trans>
					<ChevronRight className="h-4 w-4" />
				</Link>
			)}
		</nav>
	);
};
DirectoryPagination.displayName = "DirectoryPagination";

export const TherapistDirectoryPagination = (props: PaginationProps) => {
	const { t } = useLingui();

	return (
		<DirectoryPagination
			{...props}
			ariaLabel={t`Therapist directory pagination`}
		/>
	);
};
TherapistDirectoryPagination.displayName = "TherapistDirectoryPagination";
